import { Avatar, LinearProgress, Link, Stack, Button, Skeleton } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import { useTheme } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import { first, isEmpty } from 'lodash';
import { useQueryState } from 'nuqs';
import posthog from 'posthog-js';
import { useCallback, useMemo } from 'react';
import { _filters } from 'src/_mock';
import Logo from 'src/components/logo';
import { useOffSetTop } from 'src/hooks/use-off-set-top';
import { useResponsive } from 'src/hooks/use-responsive';
import { useSupabase } from 'src/hooks/use-supabase';
import { useTranslate } from 'src/locales';
import { useRouter } from 'src/routes/hooks';
import FiltersBar from 'src/sections/home/filters-bar';
import { bgBlur } from 'src/theme/css';

import { HeaderShadow } from '../_common';
import { useAuthContext } from '../../auth/hooks';
import { HEADER } from '../config-layout';

type Props = {
  showProgressBar?: boolean;
  currentProgress?: number;
  title?: string;
  withLogo?: boolean;
  hideOption?: boolean;
  withProfile?: boolean;
};

export default function Header({
  showProgressBar,
  currentProgress,
  title,
  withLogo,
  hideOption,
  withProfile,
}: Props) {
  const theme = useTheme();
  const offsetTop = useOffSetTop(HEADER.H_DESKTOP_OFFSET);
  const router = useRouter();
  const { user, loading } = useAuthContext();
  const mdUp = useResponsive('up', 'md');
  const { t } = useTranslate('common');
  const [_, setOpenLogin] = useQueryState('login');
  const { supabase } = useSupabase();

  const currentUserPhoto = useMemo(() => {
    if (isEmpty(user?.pictures)) {
      return '';
    }
    return first(user?.pictures)?.url;
  }, [user]);

  const handleTicket = useCallback(() => {
    if (!user) {
      setOpenLogin('open');
    } else {
      router.push('/me/events/?type=ticket');
    }
  }, [router, setOpenLogin, user]);

  const redirectToB2B = async () => {
    // Get the current session
    if (!user) {
      setOpenLogin('open');
    } else {
      const {
        data: { session },
        error,
      } = await supabase.auth.getSession();

      if (error || !session) {
        console.error('No active session found');
        window.location.href = '/login'; // Redirect to login if no session
        return;
      }

      const refreshToken = session.refresh_token;
      const accessToken = session.access_token;

      const redirectUrl = `https://nest.trybe.fm/auth/callback?accessToken=${accessToken}&refreshToken=${refreshToken}&next=?create-event=true`;

      window.location.href = redirectUrl;
      posthog.capture('clicked_on_create_event');
    }
  };

  return (
    <AppBar>
      <Toolbar
        disableGutters
        sx={{
          height: {
            xs: HEADER.H_MOBILE,
            md: hideOption ? 50 : HEADER.H_DESKTOP,
          },
          ...(offsetTop && {
            ...bgBlur({
              blur: 5,
              color: 'rgba(34, 34, 34, 0.5)',
            }),
            backgroundColor: 'rgba(34, 34, 34, 0.5)', // Transparent white background
            backdropFilter: 'blur(6px)', // Apply blur here
            WebkitBackdropFilter: 'blur(6px)', // Safari support
          }),
          minHeight: '44px',
          ...(mdUp &&
            !hideOption && {
              borderBottom: '2px solid #000',
            }),
          maxWidth: '100vw',
          '&.MuiToolbar-root': {
            minHeight: '44px',
          },
        }}
      >
        <Stack
          direction="column"
          sx={{
            px: { md: 4, xs: 1 },
            width: '100%',
            gap: 2,
          }}
        >
          {showProgressBar && (
            <LinearProgress
              value={currentProgress}
              variant="determinate"
              sx={{ mb: 1.5, width: 1 }}
            />
          )}
          <Stack
            flexDirection="row"
            alignItems="center"
            justifyContent={!withLogo && !withProfile && title ? 'center' : 'space-between'}
          >
            {withLogo && <Logo sx={{ mx: { sx: 1, md: 0 } }} />}

            <Stack direction="row" gap={3}>
              <Button onClick={() => redirectToB2B()} variant={mdUp ? 'text' : 'outlined'}>
                {t('buttons.create-event')}
              </Button>
              {mdUp && (
                <>
                  <Button href="/" variant="text">
                    {t('buttons.explore')}
                  </Button>

                  <Button onClick={() => handleTicket()} variant="text">
                    {t('buttons.tickets')}
                  </Button>
                  {loading && <Skeleton width={35} height={35} />}
                  {!user && !loading && (
                    <Button onClick={() => setOpenLogin('open')} variant="outlined">
                      {t('buttons.login')}
                    </Button>
                  )}
                </>
              )}
              {user && !loading && withProfile && (
                <Link href="/settings" onClick={() => posthog.capture('navbar_settings_clicked')}>
                  <Avatar
                    src={currentUserPhoto ?? ''}
                    sx={{ width: 35, height: 35, mx: 1 }}
                    variant="rounded"
                  />
                </Link>
              )}
            </Stack>
          </Stack>
          {mdUp && !hideOption && <FiltersBar filters={_filters} />}
        </Stack>
      </Toolbar>
      {offsetTop && <HeaderShadow />}
    </AppBar>
  );
}
